import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Facebook, Instagram, Twitter, Shield } from 'lucide-react';

interface SocialLoginButtonsProps {
  onSuccess: () => void;
}

export const SocialLoginButtons = ({ onSuccess }: SocialLoginButtonsProps) => {
  const [loadingProvider, setLoadingProvider] = useState<string | null>(null);
  const [availableProviders, setAvailableProviders] = useState<Set<string>>(new Set());
  const { toast } = useToast();

  // Check which providers are actually enabled
  useEffect(() => {
    const checkProviders = async () => {
      const providers = new Set<string>();
      
      // Test each provider to see if it's configured
      const testProviders = ['facebook', 'google', 'twitter'];
      
      for (const provider of testProviders) {
        try {
          // This will fail quickly if provider is not enabled
          const { error } = await supabase.auth.signInWithOAuth({
            provider: provider as any,
            options: {
              redirectTo: window.location.origin,
              skipBrowserRedirect: true // Don't actually redirect, just test
            }
          });
          
          // If no error or specific "unsupported provider" error, provider might be available
          if (!error || !error.message.includes('Unsupported provider')) {
            providers.add(provider);
          }
        } catch (e) {
          // Provider not available
        }
      }
      
      setAvailableProviders(providers);
    };

    checkProviders();
  }, []);

  const handleSocialLogin = async (provider: 'facebook' | 'google' | 'twitter') => {
    setLoadingProvider(provider);
    
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: provider,
        options: {
          redirectTo: `${window.location.origin}/`,
          queryParams: {
            auto_verify: 'true' // This will trigger auto-verification
          }
        }
      });

      if (error) {
        console.error(`${provider} login error:`, error);
        
        if (error.message.includes('Unsupported provider') || error.message.includes('provider is not enabled')) {
          // Remove this provider from available list
          setAvailableProviders(prev => {
            const newSet = new Set(prev);
            newSet.delete(provider);
            return newSet;
          });
          
          toast({
            title: "Provider Not Available",
            description: `${provider.charAt(0).toUpperCase() + provider.slice(1)} login is not configured yet.`,
            variant: "destructive"
          });
        } else {
          toast({
            title: "Login Failed",
            description: `Failed to login with ${provider}. Please try again.`,
            variant: "destructive"
          });
        }
      }
    } catch (error) {
      console.error(`${provider} login error:`, error);
      toast({
        title: "Login Failed", 
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoadingProvider(null);
    }
  };

  const handleInstagramLogin = async () => {
    // Instagram uses Facebook's OAuth with Instagram Basic Display API
    setLoadingProvider('instagram');
    
    try {
      // For now, we'll show a coming soon message for Instagram
      toast({
        title: "Coming Soon",
        description: "Instagram login will be available soon. Use Facebook login for now as they're owned by the same company.",
        variant: "default"
      });
    } finally {
      setLoadingProvider(null);
    }
  };

  // Don't render anything if no providers are available
  if (availableProviders.size === 0) {
    return null;
  }

  return (
    <div className="space-y-3">
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-background px-2 text-muted-foreground flex items-center gap-1">
            <Shield className="h-3 w-3" />
            Skip verification with social login
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-2">
        {availableProviders.has('facebook') && (
          <Button
            variant="outline"
            onClick={() => handleSocialLogin('facebook')}
            disabled={loadingProvider === 'facebook'}
            className="w-full flex items-center gap-2 hover:bg-blue-50 border-blue-200"
          >
            <Facebook className="h-4 w-4 text-blue-600" />
            {loadingProvider === 'facebook' ? 'Connecting...' : 'Continue with Facebook'}
          </Button>
        )}

        {/* Instagram is always available as a "coming soon" feature */}
        <Button
          variant="outline"
          onClick={handleInstagramLogin}
          disabled={loadingProvider === 'instagram'}
          className="w-full flex items-center gap-2 hover:bg-pink-50 border-pink-200"
        >
          <Instagram className="h-4 w-4 text-pink-600" />
          {loadingProvider === 'instagram' ? 'Connecting...' : 'Continue with Instagram'}
        </Button>

        {availableProviders.has('twitter') && (
          <Button
            variant="outline"
            onClick={() => handleSocialLogin('twitter')}
            disabled={loadingProvider === 'twitter'}
            className="w-full flex items-center gap-2 hover:bg-slate-50 border-slate-200"
          >
            <Twitter className="h-4 w-4 text-slate-900" />
            {loadingProvider === 'twitter' ? 'Connecting...' : 'Continue with X (Twitter)'}
          </Button>
        )}
      </div>

      <p className="text-xs text-center text-muted-foreground">
        Social media accounts are automatically verified ✓
      </p>
    </div>
  );
};